import React, { useMemo } from "react";
import { Box, useTheme, Chip, alpha } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { gql, useQuery } from "@apollo/client";
import { formatDisplayDate } from "../../utils/date";

const GET_REPAID_LOANS = gql`
  query {
    loanRepaymentScheduleByStatus(statusId: 3) {
      id
      loanApplicationId
      dueDate
      principalDue
      principalPaid
      interestDue
      interestPaid
      status
    }
  }
`;

const RepaidLoansTable = () => {
  const theme = useTheme();
  const { data, loading, error } = useQuery(GET_REPAID_LOANS);

  const rows = useMemo(
    () => (data?.loanRepaymentScheduleByStatus || []).map((row, index) => ({ ...row, sequence: index + 1 })),
    [data]
  );

  const columns = [
    { field: "sequence", headerName: "#", width: 70, sortable: false, filterable: false },
    {
      field: "dueDate",
      headerName: "Due Date",
      flex: 1,
      valueFormatter: ({ value }) => formatDisplayDate(value),
    },
    { field: "principalDue", headerName: "Principal Due", flex: 1 },
    { field: "principalPaid", headerName: "Principal Paid", flex: 1 },
    { field: "interestDue", headerName: "Interest Due", flex: 1 },
    { field: "interestPaid", headerName: "Interest Paid", flex: 1 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: ({ value }) => (
        <Chip size="small" color="success" label={value || "Repaid"} sx={{ fontWeight: 700 }} />
      ),
    },
  ];

  if (error) return <p>Error: {error.message}</p>;

  return (
    <Box height="75vh" sx={{
      "& .MuiDataGrid-root": { border: `1px solid ${alpha(theme.palette.secondary[200], 0.18)}`, borderRadius: "12px", overflow: "hidden" },
      "& .MuiDataGrid-cell": { borderBottom: `1px solid ${alpha(theme.palette.secondary[200], 0.08)}` },
      "& .MuiDataGrid-columnHeaders": {
        backgroundColor: theme.palette.background.alt,
        color: theme.palette.secondary[100],
        borderBottom: `1px solid ${alpha(theme.palette.secondary[200], 0.12)}`,
      },
      "& .MuiDataGrid-virtualScroller": {
        backgroundColor: theme.palette.primary.light,
      },
      "& .MuiDataGrid-footerContainer": {
        backgroundColor: theme.palette.background.alt,
        color: theme.palette.secondary[100],
        borderTop: `1px solid ${alpha(theme.palette.secondary[200], 0.12)}`,
      },
      "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
        color: `${theme.palette.secondary[200]} !important`,
      },
    }}>
      <DataGrid
        loading={loading}
        rows={rows}
        getRowId={(row) => row.id}
        columns={columns}
        rowHeight={52}
      />
    </Box>
  );
};

export default RepaidLoansTable;
